import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  CheckCircle, XCircle, Calendar, Briefcase, DollarSign, 
  MapPin, Clock, ShieldAlert, Loader2, Send 
} from 'lucide-react';
import { toast } from 'react-toastify';
import PageLoader from '../../helpers/PageLoader';
import SummaryApi from '../../common/SummaryApi';
import axios from 'axios';

const CandidateOfferRespond = () => {
  const { id } = useParams(); // Retrieves Offer ID from URL path parameters

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null); // 'accepted' | 'rejected'
  const [responseNote, setResponseNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // 👈 Added: State to capture custom inline validation errors
  const [errors, setErrors] = useState({});

  // Fetch offer specifications dynamically on mount
  const fetchOfferDetails = useCallback(async () => {
    setLoading(true);
    try {
      // Calls public-view endpoint with populated company details
      const response = await axios.get(SummaryApi.getCandidatePublicOffer.url(id));
      if (response.data.success) {
        setOffer(response.data.offer);
        if (['accepted', 'rejected'].includes(response.data.offer.status)) {
          setDecision(response.data.offer.status);
          setResponseNote(response.data.offer.candidateResponseNote || '');
        }
      }
    } catch (err) {
      toast.error('Could not locate your job offer contract. Please verify your link.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOfferDetails();
  }, [fetchOfferDetails]);

  // Currency Formatter Utility
  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(val || 0);
  };

  // 👈 Dynamic error clearing helper on input change
  const handleDecisionChange = (value) => {
    setDecision(value);
    setErrors(prev => ({ ...prev, decision: null })); // Clears decision error instantly
  };

  const handleResponseNoteChange = (e) => {
    const value = e.target.value;
    setResponseNote(value);
    if (value.trim()) {
      setErrors(prev => ({ ...prev, responseNote: null })); // Clears response note error instantly
    }
  };

  const handleDecisionSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // 1. Validation check for Decision
    if (!decision) {
      newErrors.decision = 'Please select whether you Accept or Decline this job offer.';
    }

    // 2. Validation check for Response Note (Always Required)
    if (!responseNote.trim()) {
      newErrors.responseNote = 'Remarks or comments are strictly required.';
    }

    // If there are validation errors, block submission and set error state
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.warning('Please correct the validation errors below.');
      return;
    }

    setErrors({}); // Clear existing errors
    setSubmitting(true);
    try {
      // Calls public respond API: POST /api/hr/recruitment/offers/:id/respond-offer
      const response = await axios.post(SummaryApi.respondCandidateOffer.url(id), {
        decision,
        responseNote
      });

      if (response.data.success) {
        toast.success(decision === 'accepted' ? 'Congratulations! You have accepted the job offer.' : 'You have declined the job offer.');
        fetchOfferDetails(); // Reload page to show locked success panel
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failure');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <PageLoader />;
  if (!offer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center max-w-md w-full shadow-lg space-y-4">
          <ShieldAlert className="h-12 w-12 text-primary mx-auto" />
          <h2 className="font-bold text-gray-900 text-base">Invalid Offer Link</h2>
          <p className="text-xs text-gray-400">This job offer package link is either expired, invalid, or belongs to another organization.</p>
        </div>
      </div>
    );
  }

  const candidate = offer.candidateId || {};
  const organization = offer.organizationId || {};
  const isLocked = ['accepted', 'rejected', 'withdrawn', 'expired'].includes(offer.status);
  const roleName = offer.designationId?.name || offer.designationId?.title || "Assigned Position";
  const deptName = offer.departmentId?.name || offer.departmentId?.departmentName || "N/A";

  // Helper to render dynamic organization avatar if logo Url is missing
  const renderOrgLogo = () => {
    if (organization.organizationPicture) {
      return (
        <img 
          src={organization.organizationPicture} 
          alt={organization.name} 
          className="h-12 w-12 object-contain rounded-lg border bg-white p-1"
        />
      );
    }
    return (
      <div className="h-12 w-12 rounded-lg bg-secondary text-white flex items-center justify-center font-bold text-lg shadow-sm border border-blue-900">
        {(organization.name || 'ORG').substring(0, 1).toUpperCase()}
      </div>
    );
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'sent': return 'bg-blue-50 text-secondary border-blue-200';
      case 'accepted': return 'bg-green-50 text-green-700 border-green-200';
      case 'rejected': return 'bg-red-50 text-primary border-red-200';
      case 'withdrawn': return 'bg-amber-50 text-amber-700 border-amber-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  return (
    <div className="bg-gray-50/50 py-10 px-4 md:px-8 flex flex-col justify-center items-center text-gray-700">
      <div className="max-w-7xl w-full space-y-6">
        
        {/* Official Company Branding Header block */}
        <div className="bg-white border border-gray-200 p-4 md:p-6 rounded-xl shadow-sm flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            {renderOrgLogo()}
            <div>
              <h2 className="text-sm font-bold text-secondary uppercase tracking-wider">{organization.name || 'Our Organization'}</h2>
              <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest block mt-0.5">Official Employment Offer Portal</span>
            </div>
          </div>
          
          {/* Dynamic Status badge utilizing getStatusBadgeClass cleanly */}
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className="text-[10px] text-gray-400 font-bold bg-gray-50 border border-gray-200 px-3 py-1 rounded-full uppercase">
              Confidential Offer Letter
            </span>
            {isLocked && (
              <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase border ${getStatusBadgeClass(offer.status)}`}>
                {offer.status}
              </span>
            )}
          </div>
        </div>

        {/* Congratulations Header Banner using Brand Dark Blue (Only shown if NOT locked) */}
        {!isLocked && (
          <div className="bg-secondary text-white p-6 md:p-10 rounded-xl shadow-sm text-center space-y-2 border border-blue-900">
            <h1 className="text-xl md:text-3xl font-extrabold tracking-tight capitalize text-white">Congratulations, {candidate.firstName}! 🎉</h1>
            <p className="text-xs md:text-sm text-blue-100 max-w-xl mx-auto">
              We are incredibly excited to invite you to join our growing team at <strong className="text-white">{organization.name}</strong>. Please review the official job contract specifications below.
            </p>
          </div>
        )}

        {/* Dynamic Locked Layout: Clean Light Theme confirmation screen replacing everything once accepted/rejected */}
        {isLocked ? (
          <div className="bg-white border border-gray-200 rounded-xl p-8 md:p-12 shadow-sm text-center max-w-7xl mx-auto space-y-6">
            {offer.status === 'accepted' ? (
              <div className="space-y-4">
                <div className="h-16 w-16 bg-red-50 border border-red-200 text-primary rounded-full flex items-center justify-center mx-auto shadow-sm">
                  <CheckCircle className="h-10 w-10 text-primary" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-xl font-extrabold text-secondary">Offer Letter Accepted!</h2>
                  <p className="text-xs text-gray-400 max-w-md mx-auto leading-relaxed">
                    Thank you, <strong className="text-gray-800 capitalize">{candidate.firstName}</strong>! You have officially accepted our employment offer. Our HR division has initiated your automated onboarding roadmap.
                  </p>
                </div>
              </div>
            ) : offer.status === 'rejected' ? (
              <div className="space-y-4">
                <div className="h-16 w-16 bg-gray-100 border border-gray-200 text-gray-500 rounded-full flex items-center justify-center mx-auto shadow-sm">
                  <XCircle className="h-10 w-10 text-gray-500" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-xl font-extrabold text-gray-700">Offer Package Declined</h2>
                  <p className="text-xs text-gray-400 max-w-md mx-auto leading-relaxed">
                    You have chosen to decline this employment offer package. We sincerely respect your decision and wish you the very best in your professional journey.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="h-16 w-16 bg-amber-50 border border-amber-200 text-amber-500 rounded-full flex items-center justify-center mx-auto shadow-sm">
                  <ShieldAlert className="h-10 w-10 text-amber-500" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-xl font-extrabold text-amber-600">Offer Unavailable</h2>
                  <p className="text-xs text-gray-400 max-w-md mx-auto leading-relaxed">
                    This employment offer package is no longer active. It has been either withdrawn, expired, or deactivated.
                  </p>
                </div>
              </div>
            )}

            {/* Structured Compact Contract Summary */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 max-w-md mx-auto grid grid-cols-2 gap-4 text-left text-xs">
              <div>
                <span className="text-[10px] text-gray-400 block font-semibold">Role Title</span>
                <span className="font-bold text-secondary">{roleName}</span>
              </div>
              <div>
                <span className="text-[10px] text-gray-400 block font-semibold">Department</span>
                <span className="font-bold text-secondary">{deptName}</span>
              </div>
              <div className="mt-1">
                <span className="text-[10px] text-gray-400 block font-semibold">Joining Date</span>
                <span className="font-bold text-gray-800">{new Date(offer.joiningDate).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</span>
              </div>
              <div className="mt-1">
                <span className="text-[10px] text-gray-400 block font-semibold">Annual CTC</span>
                <span className="font-bold text-primary">{formatCurrency(offer.ctc)}</span>
              </div>
            </div>

            {offer.candidateResponseNote && (
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl text-left text-xs text-gray-500 max-w-md mx-auto space-y-1">
                <span className="text-[9px] font-bold text-gray-400 uppercase block">Your Submitted Remarks</span>
                <p className="italic">" {offer.candidateResponseNote} "</p>
              </div>
            )}
          </div>
        ) : (
          /* Editable Response Workspace Grid (Only displayed if status is sent/pending) */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Left Area: Contract details & Modern Compensation Table (Takes 2/3 width) */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Offer Brief details card */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
                <h2 className="text-[10px] font-bold text-secondary uppercase tracking-wider border-b pb-2 flex items-center gap-1.5">
                  <Briefcase className="h-4 w-4 text-secondary" /> Position Configuration
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="text-gray-400 block font-semibold">Position Offered:</span>
                    <span className="font-bold text-secondary text-sm mt-0.5 block">{roleName}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block font-semibold">Department:</span>
                    <span className="font-bold text-secondary text-sm mt-0.5 block">{deptName}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block font-semibold">Employment Type:</span>
                    <span className="font-bold text-gray-700 capitalize mt-0.5 block">{offer.employmentType?.replace('_', ' ')}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block font-semibold">Probation Period:</span>
                    <span className="font-bold text-gray-700 mt-0.5 block">{offer.probationPeriodMonths} Months</span>
                  </div>
                </div>
              </div>

              {/* Compensation Breakup Modern Table */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
                <h2 className="text-[10px] font-bold text-secondary uppercase tracking-wider border-b pb-2 flex items-center gap-1.5">
                  <DollarSign className="h-4 w-4 text-secondary" /> Annual Compensation Structure
                </h2>
                
                {offer.salaryBreakup?.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden bg-white">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-gray-50 border-b text-gray-400 font-bold text-[9px] uppercase">
                          <th className="p-3">Salary Component</th>
                          <th className="p-3 text-right">Amount (Annual)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y text-gray-600">
                        {offer.salaryBreakup.map((comp, idx) => (
                          <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                            <td className="p-3 font-semibold">{comp.component}</td>
                            <td className="p-3 text-right font-bold text-gray-800">{formatCurrency(comp.amount)}</td>
                          </tr>
                        ))}
                        {/* Cumulative total row with primary brand red CTC text */}
                        <tr className="bg-gray-50 font-extrabold text-gray-950">
                          <td className="p-3">Total Cost to Company (CTC)</td>
                          <td className="p-3 text-right text-primary text-sm">{formatCurrency(offer.ctc)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="p-4 bg-gray-50 rounded-lg text-center text-xs text-gray-400 italic">No breakdown details specified.</p>
                )}
              </div>

            </div>

            {/* Right Area: Form responses and Submission Panel (Takes 1/3 width) */}
            <div className="space-y-6">
              
              {/* Offer Status & Timeline Card */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4 text-xs">
                <h2 className="text-[10px] font-bold text-secondary uppercase tracking-wider border-b pb-2">Target Milestones</h2>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="h-4 w-4 text-secondary shrink-0" />
                    <span>Target Joining Date: <strong className="text-gray-850">{new Date(offer.joiningDate).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</strong></span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="h-4 w-4 text-secondary shrink-0" />
                    <span>Offer Expiry Date: <strong className="text-gray-850">{new Date(offer.expiryDate).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</strong></span>
                  </div>
                  {offer.workLocation && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="h-4 w-4 text-secondary shrink-0" />
                      <span>Location: <strong className="text-gray-850">{offer.workLocation}</strong></span>
                    </div>
                  )}
                </div>
              </div>

              {/* Editable Response Form (Only rendered if status is sent/pending) */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
                <h3 className="text-[10px] font-bold text-secondary uppercase tracking-wider border-b pb-2">Your Decision</h3>
                
                {/* 👈 Added: noValidate attribute prevents browser default tooltip errors */}
                <form onSubmit={handleDecisionSubmit} noValidate className="space-y-4">
                  
                  {/* Option Selector Cards with Red & Navy brand highlights */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => handleDecisionChange('accepted')} // Clears error instantly on change
                      className={`flex flex-col items-center justify-center p-3 border rounded-xl transition-all ${
                        decision === 'accepted' 
                          ? 'border-primary bg-red-50/20 text-primary ring-2 ring-primary' 
                          : 'border-gray-200 hover:bg-gray-50 text-gray-500'
                      }`}
                    >
                      <CheckCircle className="h-5 w-5 mb-1" />
                      <span className="text-xs font-bold">Accept Offer</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDecisionChange('rejected')} // Clears error instantly on change
                      className={`flex flex-col items-center justify-center p-3 border rounded-xl transition-all ${
                        decision === 'rejected' 
                          ? 'border-secondary bg-blue-50/20 text-secondary ring-2 ring-secondary' 
                          : 'border-gray-200 hover:bg-gray-50 text-gray-500'
                      }`}
                    >
                      <XCircle className="h-5 w-5 mb-1" />
                      <span className="text-xs font-bold">Decline Offer</span>
                    </button>
                  </div>

                  {/* 👈 Added: Inline validation error message for Decision */}
                  {errors.decision && (
                    <p className="text-xs font-semibold text-primary animate-pulse mt-1.5">
                      {errors.decision}
                    </p>
                  )}

                  {/* Response Text Note (Globally Required for both decisions) */}
                  <div>
                    <label className="block text-[11px] font-bold text-gray-500 mb-1">
                      Response Note / Remarks *
                    </label>
                    <textarea
                      rows={4}
                      value={responseNote}
                      onChange={handleResponseNoteChange} // Clears error dynamically on typing
                      placeholder="Please enter your comments or remarks here... (Required)"
                      className={`w-full border rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-primary text-gray-800 bg-white ${
                        errors.responseNote ? 'border-primary' : 'border-gray-200'
                      }`}
                    />
                    
                    {/* 👈 Added: Inline validation error message for Response Note */}
                    {errors.responseNote && (
                      <p className="text-xs font-semibold text-primary animate-pulse mt-1.5">
                        {errors.responseNote}
                      </p>
                    )}
                  </div>

                  {/* Submit Trigger button utilizing brand Red and Navy blue properly */}
                  <button
                    type="submit"
                    disabled={submitting || !decision}
                    className={`w-full text-white py-2.5 rounded-lg text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1.5 transition-all shadow-sm ${
                      decision === 'rejected' ? 'bg-secondary hover:bg-blue-900' : 'bg-primary hover:bg-red-700'
                    }`}
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    <Send className="h-3.5 w-3.5" /> {submitting ? 'Submitting...' : 'Register Response'}
                  </button>
                </form>
              </div>

            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default CandidateOfferRespond;